import { useCallback, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";

import { useLogout } from "@refinedev/core";

import { InactivityTimeoutModal } from "./InactivityTimeoutModal";

// 30 minutes total inactivity timeout
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
// Show warning 3 minutes before expiry
const PROMPT_BEFORE_IDLE_MS = 3 * 60 * 1000;

/**
 * Controller component that tracks user inactivity using react-idle-timer.
 * Shows a warning modal 3 minutes before session expiry. If the user
 * does not respond, automatically logs them out with a session-expired redirect.
 */
export const InactivityTimeoutOverlay = () => {
  const { mutate: logout } = useLogout();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(PROMPT_BEFORE_IDLE_MS / 1000);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdownInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleIdle = useCallback(() => {
    setShowWarning(false);
    clearCountdownInterval();
    const currentPath = window.location.pathname + window.location.search;
    logout({ redirectPath: `/login?reason=session_expired&to=${encodeURIComponent(currentPath)}` });
  }, [logout, clearCountdownInterval]);

  const handlePrompt = useCallback(() => {
    setShowWarning(true);
  }, []);

  const handleActive = useCallback(() => {
    setShowWarning(false);
    clearCountdownInterval();
  }, [clearCountdownInterval]);

  const { activate, getRemainingTime } = useIdleTimer({
    onIdle: handleIdle,
    onPrompt: handlePrompt,
    onActive: handleActive,
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: PROMPT_BEFORE_IDLE_MS,
    throttle: 500,
  });

  // When tab becomes visible again, check if timeout was missed while backgrounded
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const remaining = getRemainingTime();
        if (remaining <= 0) {
          handleIdle();
        } else if (remaining <= PROMPT_BEFORE_IDLE_MS) {
          setShowWarning(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [getRemainingTime, handleIdle]);

  // Update countdown every second while warning is shown
  useEffect(() => {
    if (showWarning) {
      const updateRemaining = () => {
        const ms = getRemainingTime();
        setRemainingSeconds(Math.ceil(ms / 1000));
      };
      updateRemaining();
      intervalRef.current = setInterval(updateRemaining, 1000);
    } else {
      clearCountdownInterval();
    }

    return () => clearCountdownInterval();
  }, [showWarning, getRemainingTime, clearCountdownInterval]);

  const handleStillHere = () => {
    setShowWarning(false);
    activate();
  };

  const handleLogOut = () => {
    setShowWarning(false);
    clearCountdownInterval();
    logout();
  };

  return (
    <InactivityTimeoutModal
      open={showWarning}
      remainingSeconds={remainingSeconds}
      onStillHere={handleStillHere}
      onLogOut={handleLogOut}
    />
  );
};
